import { Component, Inject, Input, OnInit } from '@angular/core';
import { countBy, filter, find, forEach, map, sortBy } from 'lodash';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { DateHelpers } from 'Src/ng2/shared/services/date-helpers/date-helpers.service';
import { unsubscribeComponent } from '../../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ImUser } from '../../../../shared/services/im-models/im-user';
import { ISchoolAssessment } from '../../../../shared/typings/interfaces/school-assessment.interface';
import { IStudentAssessment } from '../../../../shared/typings/interfaces/student-assessment.interface';
import { IUser } from '../../../../shared/typings/interfaces/user.interface';

export interface IAssessmentTableRow {
  schoolAssessmentId: string;
  adminNumber: number;
  startDate: string;
  endDate: string;
  lastEditedOn: string;
  lastEditedBy: string;
  progress: string;
}

export interface IAssessmentTableSection {
  section: string;
  admins: IAssessmentTableRow[];
}

@unsubscribeComponent
@Component({
  selector: 'assessments-data-mid-level',
  template: require('./assessments-data-mid-level.component.html'),
  styles: [require('./assessments-data-mid-level.component.scss')],
})
export class AssessmentsDataMidLevelComponent implements OnInit {
  @Input() schoolAssessments$: Observable<ISchoolAssessment[]>;
  @Input() studentAssessments$: Observable<IStudentAssessment[]>;
  @Input() currentUser: IUser;
  @Input() schoolId: string;

  private dataSubscription: Subscription;
  public schoolAssessments: ISchoolAssessment[];
  public displayBoxShadow: boolean = false;
  public finalized: boolean = false;
  public isAdmin: boolean;
  public isEditingUser: boolean;
  public tableData: IAssessmentTableSection[];

  constructor(
    @Inject('$state') private $state,
    @Inject('SpinnerModal') private spinnerModal,
    private imUser: ImUser,
    private dateHelpers: DateHelpers,
  ) {
    //
  }

  ngOnInit(): void {
    this.isAdmin = this.imUser.isAdmin(this.currentUser);
    this.isEditingUser = this.imUser.isEditingUser(this.currentUser);

    // subscribe to data;
    this.dataSubscription = combineLatest(this.schoolAssessments$, this.studentAssessments$).subscribe(
      ([schoolAssessments, studentAssessments]) => {
        const sortedSchoolAssessments = sortBy(schoolAssessments, schoolAssessment => schoolAssessment.adminNumber);
        this.finalized = !!find(schoolAssessments, schoolAssessment => schoolAssessment.status === 'LOCKED');
        this.tableData = this.setTableData(sortedSchoolAssessments, studentAssessments);
      },
    );
  }

  goToSettings(): void {
    this.$state.go('main.school.settings.assessments');
  }

  setTableData(
    schoolAssessments: ISchoolAssessment[],
    studentAssessments: IStudentAssessment[],
  ): IAssessmentTableSection[] {
    const assessmentTables: IAssessmentTableSection[] = [];
    // get unique assessments
    const countedAssessments = countBy(schoolAssessments, schoolAssessment => {
      return schoolAssessment.assessment.assessmentNickName;
    });
    const assessmentNames = Object.keys(countedAssessments);
    // for each unique assessment name, create an IAssessmentTableSection
    // currently only 'F & P' will be an option (JE)
    forEach(assessmentNames, section => {
      const admins = map(schoolAssessments, schoolAssessment => {
        const {
          assessment: { assessmentNickName },
        } = schoolAssessment;
        if (assessmentNickName === section) {
          // get progress data from studentAssessments
          return this.parseAdminData(schoolAssessment, studentAssessments);
        }
      });
      const assessmentTableSection = {
        section,
        admins,
      };
      assessmentTables.push(assessmentTableSection);
    });
    return assessmentTables;
  }

  parseAdminData(schoolAssessment: ISchoolAssessment, studentAssessments: IStudentAssessment[]): IAssessmentTableRow {
    const {
      _id,
      assessment: { assessmentNickName },
      adminNumber,
      startDate,
      endDate,
      lastEditedOn,
      lastEditedBy,
    } = schoolAssessment;
    // get progress data from studentAssessments
    const studAssmntsForAdmin = filter(studentAssessments, studentAssessment => {
      const { schoolAssessment } = studentAssessment;
      const nameMatch = schoolAssessment.assessmentNickName === assessmentNickName;
      const adminMatch = schoolAssessment.adminNumber === adminNumber;
      return nameMatch && adminMatch;
    });
    const completed = filter(studAssmntsForAdmin, studAssmnt => studAssmnt.status === 'COMPLETED');
    const admin: IAssessmentTableRow = {
      schoolAssessmentId: _id,
      adminNumber,
      startDate: this.dateHelpers.getFormattedMoment(startDate, 'MMM DD'),
      endDate: this.dateHelpers.getFormattedMoment(endDate, 'MMM DD'),
      lastEditedOn: lastEditedOn ? this.dateHelpers.getFormattedMoment(lastEditedOn, 'MMM DD') : '-',
      lastEditedBy: lastEditedBy ? `${lastEditedBy.firstName.substring(0, 1)}. ${lastEditedBy.lastName}` : '-',
      progress: completed.length ? `${completed.length} of ${studAssmntsForAdmin.length}` : '-',
    };
    return admin;
  }

  rowClick(admin: IAssessmentTableRow) {
    if (this.isEditingUser) {
      this.spinnerModal.activateSpinner();
      this.$state.go('main.school.data-entry.admin-input', { schoolAssessmentId: admin.schoolAssessmentId });
    }
  }
}
